import React, { useState, useEffect } from 'react';
import { get } from '../../utility/fetch';
import notification from '../../utility/notification';

const BannedMedications = () => {
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [bannedDrugs, setBannedDrugs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'banned', 'decommissioned'

    // Sample countries - Replace with API call if you have a countries endpoint
    const availableCountries = [
        { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
        { code: 'US', name: 'United States', flag: '🇺🇸' },
        { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
        { code: 'CA', name: 'Canada', flag: '🇨🇦' },
        { code: 'AU', name: 'Australia', flag: '🇦🇺' },
        { code: 'IN', name: 'India', flag: '🇮🇳' },
        { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
        { code: 'KE', name: 'Kenya', flag: '🇰🇪' },
        { code: 'GH', name: 'Ghana', flag: '🇬🇭' },
        { code: 'DE', name: 'Germany', flag: '🇩🇪' },
        { code: 'FR', name: 'France', flag: '🇫🇷' },
        { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
    ];

    useEffect(() => {
        setCountries(availableCountries);
    }, []);

    useEffect(() => {
        if (selectedCountry) {
            fetchBannedMedications(selectedCountry);
        }
    }, [selectedCountry]);

    const fetchBannedMedications = async (countryCode) => {
        setLoading(true);
        try {
            // Replace with your actual API endpoint
            const response = await get(`/medications/banned?country=${countryCode}`);
            
            if (response?.data) {
                setBannedDrugs(response.data);
            } else {
                // Mock data for demonstration - Remove this when API is ready
                setBannedDrugs(generateMockData(countryCode));
            }
        } catch (error) {
            console.error('Error fetching banned medications:', error);
            // Use mock data on error for demonstration
            setBannedDrugs(generateMockData(countryCode));
            notification({
                message: "Using sample data. Connect to API for real-time information.",
                type: "info"
            });
        } finally {
            setLoading(false);
        }
    };

    // Mock data generator - Remove this when you have real API
    const generateMockData = (countryCode) => {
        const mockDrugs = {
            'NG': [
                { id: 1, name: 'Codeine Syrup', genericName: 'Codeine Phosphate', status: 'banned', reason: 'Abuse and addiction concerns', bannedDate: '2018-05-01', category: 'Analgesic' },
                { id: 2, name: 'Tramadol (High Dose)', genericName: 'Tramadol HCl', status: 'banned', reason: 'Substance abuse', bannedDate: '2018-05-01', category: 'Opioid Analgesic' },
                { id: 3, name: 'Chloramphenicol Eye Drops', genericName: 'Chloramphenicol', status: 'decommissioned', reason: 'Safety concerns', bannedDate: '2019-03-15', category: 'Antibiotic' },
                { id: 4, name: 'Analgin', genericName: 'Metamizole Sodium', status: 'banned', reason: 'Risk of agranulocytosis', bannedDate: '2016-11-20', category: 'Analgesic' },
            ],
            'US': [
                { id: 5, name: 'Thalidomide', genericName: 'Thalidomide', status: 'banned', reason: 'Teratogenic effects', bannedDate: '1962-03-08', category: 'Sedative' },
                { id: 6, name: 'Rofecoxib (Vioxx)', genericName: 'Rofecoxib', status: 'decommissioned', reason: 'Cardiovascular risks', bannedDate: '2004-09-30', category: 'NSAID' },
                { id: 7, name: 'Phenylpropanolamine', genericName: 'Phenylpropanolamine HCl', status: 'banned', reason: 'Stroke risk', bannedDate: '2000-11-06', category: 'Decongestant' },
            ],
            'GB': [
                { id: 8, name: 'Sibutramine', genericName: 'Sibutramine HCl', status: 'banned', reason: 'Cardiovascular adverse effects', bannedDate: '2010-01-21', category: 'Appetite Suppressant' },
                { id: 9, name: 'Rosiglitazone', genericName: 'Rosiglitazone Maleate', status: 'decommissioned', reason: 'Heart attack risk', bannedDate: '2010-09-23', category: 'Antidiabetic' },
            ],
            'IN': [
                { id: 10, name: 'Nimesulide (for children)', genericName: 'Nimesulide', status: 'banned', reason: 'Liver toxicity in children', bannedDate: '2011-02-10', category: 'NSAID' },
                { id: 11, name: 'Oxyphenbutazone', genericName: 'Oxyphenbutazone', status: 'banned', reason: 'Serious adverse effects', bannedDate: '2013-06-11', category: 'NSAID' },
            ]
        };

        return mockDrugs[countryCode] || [];
    };

    const filteredDrugs = bannedDrugs.filter(drug => {
        const matchesSearch = drug.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            drug.genericName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || drug.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    const selectedCountryData = countries.find(c => c.code === selectedCountry);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Header Section */}
            <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                    {/* Warning Icon SVG */}
                    <svg className="w-6 h-6 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16v-2h2v2h-2zm0-4V8h2v4h-2z"/>
                    </svg>
                    <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                        Banned & Decommissioned Medications
                    </h1>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                    Search for medications that have been banned or decommissioned by country.
                    This information helps healthcare professionals and patients stay informed about drug safety.
                </p>
            </div>

            {/* Controls Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                {/* Country Selection */}
                <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        {/* Globe Icon SVG */}
                        <svg className="w-5 h-5 text-[#3c7e2d]" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                        </svg>
                        Select Country
                    </label>
                    <div className="relative">
                        <select
                            className="w-full px-4 py-3 pr-10 text-base border-2 border-gray-300 rounded-xl bg-white cursor-pointer transition-all duration-300 hover:border-[#3c7e2d] focus:border-[#3c7e2d] focus:ring-4 focus:ring-[#3c7e2d] focus:ring-opacity-20 outline-none appearance-none"
                            value={selectedCountry}
                            onChange={(e) => setSelectedCountry(e.target.value)}
                        >
                            <option value="">Choose a country...</option>
                            {countries.map(country => (
                                <option key={country.code} value={country.code}>
                                    {country.flag} {country.name}
                                </option>
                            ))}
                        </select>
                        {/* Dropdown Arrow SVG */}
                        <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7 10l5 5 5-5z"/>
                        </svg>
                    </div>
                   
                </div>

                {/* Search and Filter */}
                {selectedCountry && (
                    <div className="lg:col-span-2 space-y-2 w-full mt-4">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                            {/* Search Icon SVG */}
                            <svg className="w-5 h-5 text-[#3c7e2d]" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                            </svg>
                            Search Medications
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                className="w-full px-4 py-3 pl-11 text-base border-2 border-gray-300 rounded-xl transition-all duration-300 hover:border-[#3c7e2d] focus:border-[#3c7e2d] focus:ring-4 focus:ring-[#3c7e2d] focus:ring-opacity-20 outline-none"
                                placeholder="Search by drug name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ border: '2px solid #9ca3af' }}

                            />
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                            </svg>
                        </div>
                        
                        {/* Filter Buttons */}
                        <div className="flex flex-wrap gap-2 mt-4">
                            <button
                                onClick={() => setFilterStatus('all')}
                                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                                    filterStatus === 'all'
                                        ? 'bg-[#3c7e2d] text-white shadow-md'
                                        : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-[#3c7e2d]'
                                }`}
                            >
                                All
                            </button>
                            <button
                                onClick={() => setFilterStatus('banned')}
                                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                                    filterStatus === 'banned'
                                        ? 'bg-[#3c7e2d] text-white shadow-md'
                                        : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-[#3c7e2d]'
                                }`}
                            >
                                Banned
                            </button>
                            <button
                                onClick={() => setFilterStatus('decommissioned')}
                                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                                    filterStatus === 'decommissioned'
                                        ? 'bg-[#3c7e2d] text-white shadow-md'
                                        : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-[#3c7e2d]'
                                }`}
                            >
                                Decommissioned
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Statistics Bar */}
            {selectedCountry && bannedDrugs.length > 0 && (
                <div className="gap-3 mb-6 p-3 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl" style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 w-full">
                        <div className="flex items-center gap-2">
                            {/* Medication Icon SVG */}
                            <svg className="w-6 h-6 text-[#3c7e2d] flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                            </svg>
                            <div className="flex-1">
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Total</p>
                                <p className="text-2xl font-bold text-[#3c7e2d]">{bannedDrugs.length}</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 w-full">
                        <div className="flex items-center gap-2">
                            {/* Ban Icon SVG */}
                            <svg className="w-6 h-6 text-red-600 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z"/>
                            </svg>
                            <div className="flex-1">
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Banned</p>
                                <p className="text-2xl font-bold text-red-600">
                                    {bannedDrugs.filter(d => d.status === 'banned').length}
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 w-full">
                        <div className="flex items-center gap-2">
                            {/* Decommission Icon SVG */}
                            <svg className="w-6 h-6 text-orange-600 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 5.99L19.53 19H4.47L12 5.99M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z"/>
                            </svg>
                            <div className="flex-1">
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Decommissioned</p>
                                <p className="text-2xl font-bold text-orange-600">
                                    {bannedDrugs.filter(d => d.status === 'decommissioned').length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="relative">
                        <div className="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
                        <div className="w-16 h-16 border-4 border-[#3c7e2d] border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                    </div>
                    <p className="mt-4 text-gray-600 font-medium">Loading medications...</p>
                </div>
            ) : selectedCountry && filteredDrugs.length > 0 ? (
                /* Drug List */
                <div className="space-y-3">
                    {filteredDrugs.map(drug => (
                        <div 
                            key={drug.id} 
                            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md hover:border-[#3c7e2d] transition-all duration-300"
                        >
                            {/* Drug Header */}
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                                <div className="flex-1">
                                    <h3 className="text-base font-bold text-gray-900 mb-0.5">{drug.name}</h3>
                                    <p className="text-xs text-gray-600 italic">{drug.genericName}</p>
                                </div>
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                                    drug.status === 'banned' 
                                        ? 'bg-red-100 text-red-700' 
                                        : 'bg-orange-100 text-orange-700'
                                }`}>
                                    {drug.status === 'banned' ? (
                                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z"/>
                                        </svg>
                                    ) : (
                                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 5.99L19.53 19H4.47L12 5.99M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z"/>
                                        </svg>
                                    )}
                                    {drug.status}
                                </span>
                            </div>

                            {/* Drug Details Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-gray-100">
                                <div className="flex items-start gap-2">
                                    <svg className="w-4 h-4 text-[#3c7e2d] mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                    </svg>
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase">Category</p>
                                        <p className="text-sm text-gray-900 font-medium">{drug.category}</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start gap-2">
                                    <svg className="w-4 h-4 text-[#3c7e2d] mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                                    </svg>
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase">Date Banned</p>
                                        <p className="text-sm text-gray-900 font-medium">
                                            {new Date(drug.bannedDate).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                            })}
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="md:col-span-2 flex items-start gap-2">
                                    <svg className="w-4 h-4 text-[#3c7e2d] mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                                    </svg>
                                    <div className="flex-1">
                                        <p className="text-xs font-semibold text-gray-500 uppercase">Reason</p>
                                        <p className="text-sm text-gray-700">{drug.reason}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : selectedCountry && filteredDrugs.length === 0 ? (
                /* No Results State */
                <div className="text-center py-16">
                    <svg className="w-24 h-24 mx-auto text-gray-300 mb-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                    </svg>
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No medications found</h3>
                    <p className="text-gray-500">
                        {searchTerm
                            ? `No medications matching "${searchTerm}" were found`
                            : 'No banned or decommissioned medications found for this country'}
                    </p>
                </div>
            ) : (
                /* Initial State */
                <div className="text-center py-16">
                    <svg className="w-32 h-32 mx-auto text-gray-300 mb-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                    </svg>
                    <h3 className="text-2xl font-semibold text-gray-700 mb-3">Select a Country</h3>
                    <p className="text-gray-500 text-lg">
                        Choose a country from the dropdown to view its banned and decommissioned medications
                    </p>
                </div>
            )}
        </div>
    );
};

export default BannedMedications;
